import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plane, CreditCard, CheckCircle2, TrendingDown, DollarSign, Users, ArrowRight, Lightbulb, ExternalLink, Shield, Clock, Globe, Award, Zap, Star, Wallet, Gift, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function FlightInstallmentsBNPLPaymentOptions() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // PayPal comparison table
  const paypalComparison = [
    {
      option: 'Pay in 4',
      schedule: '4 payments, every 2 weeks',
      interest: '0% interest',
      upfront: 'First payment at purchase',
      eligibility: 'Approval at checkout; soft check may be needed'
    },
    {
      option: 'Pay Monthly',
      schedule: 'Fixed monthly payments over 3–24 months',
      interest: 'Fixed APR (0%–35.99%)',
      upfront: '$0',
      eligibility: 'Subject to credit approval'
    }
  ];

  // BNPL providers table
  const bnplProviders = [
    {
      provider: 'Defera',
      structure: 'Deposit, then weekly or bi-weekly payments',
      interest: 'No interest; fees included in the trip total',
      where: 'Major airlines worldwide',
      features: 'No credit check; trip price secured once your booking is approved'
    },
    {
      provider: 'Affirm',
      structure: '3–60 months; also pay-in-4',
      interest: '0%–36% APR; Pay in 4 is 0%',
      where: 'Airlines and OTAs',
      features: 'Soft check to see your plans; no late fees'
    },
    {
      provider: 'Klarna',
      structure: 'Pay in 4; up to 24 months',
      interest: 'Interest-free short-term; financing APR disclosed',
      where: 'OTAs, travel sites',
      features: 'In-app management; flexible timelines (Travel + Leisure)'
    },
    {
      provider: 'PayPal',
      structure: 'Pay in 4; Pay Monthly',
      interest: 'Interest-free vs. fixed APR',
      where: 'Widely accepted',
      features: '$0 down for Pay Monthly; broad reach (Skyscanner\'s BNPL guide)'
    },
    {
      provider: 'Alternative Airlines',
      structure: 'Multiple BNPLs (Affirm, Klarna, etc.)',
      interest: 'Varies by partner',
      where: '600+ airlines via agency checkout',
      features: 'One-stop BNPL flight booking (Alternative Airlines: Fly Now Pay Later)'
    }
  ];

  // Quick chooser table
  const quickChooser = [
    {
      situation: 'No credit history or bad credit',
      options: 'Debit; layaway; select BNPL with soft checks',
      why: 'Avoids hard pulls; predictable payments'
    },
    {
      situation: 'Want to book today, pay over time',
      options: 'BNPL (Affirm/Klarna/PayPal); layaway-style plans like Defera',
      why: 'Spread the cost; compare credit checks and interest'
    },
    {
      situation: 'Prefer fixed, interest-free installments',
      options: 'PayPal Pay in 4; pay-in-4 via BNPL partners',
      why: '0% interest if you qualify'
    },
    {
      situation: 'Want to cap spending upfront',
      options: 'Airline gift cards; debit',
      why: 'Hard budget limit'
    },
    {
      situation: 'Unbanked or cash-preferred',
      options: 'Cash where an airline still takes it (rare; check first)',
      why: 'No card needed'
    },
    {
      situation: 'Protect price but pay later',
      options: 'Deposit-based layaway (e.g., Defera)',
      why: 'Lock fare, pay gradually'
    },
    {
      situation: 'Redeem rewards, minimize cash',
      options: 'Airline miles/points',
      why: 'Low out-of-pocket'
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: 'Can I book flights now and pay later without a credit card?',
      answer: 'Yes. Options include BNPL services, PayPal installments, and deposit-based layaway plans that let you complete payments over time without using a credit card.'
    },
    {
      question: 'Which airlines or platforms let me pay for flights in installments?',
      answer: 'Airlines and OTAs increasingly offer BNPL at checkout, and travel agencies like Alternative Airlines aggregate multiple installment providers in one place.'
    },
    {
      question: 'How do installment and layaway plans differ for airline tickets?',
      answer: 'Installment plans vary by provider. With most BNPL, a lender pays the airline or travel site at checkout, so your booking is confirmed right away while you repay the lender. With Defera, your agreed-upon trip price is secured once your booking is approved, and your airline confirmation number arrives once the plan is paid in full. Traditional layaway usually confirms the booking only after the final payment.'
    },
    {
      question: 'Can I finance flights without a credit check or impact on my credit score?',
      answer: 'Some layaway services and select BNPL options use no credit check or soft checks only; always review each provider\'s eligibility policy first.'
    },
    {
      question: 'What happens if I miss a payment on a flight financing plan?',
      answer: 'You may face late fees, canceled plans, or loss of deposits, depending on the provider—read terms carefully and set up autopay to stay on track.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/flight-installments-bnpl-payment-options"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Best Ways to Book Flights on Installments Without Credit Cards",
            "description": "Learn how to finance airline tickets with BNPL apps, debit cards, PayPal installments, and flight-specific payment plans—no credit card needed.",
            "author": {
              "@type": "Organization",
              "name": "Defera"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Defera",
              "logo": {
                "@type": "ImageObject",
                "url": "https://defera.app/defera-transparent.png"
              }
            },
            "datePublished": "2024-12-12",
            "dateModified": "2026-11-03",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://defera.app/guides/flight-installments-bnpl-payment-options"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link 
              to="/guides" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero Section */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-white/[0.08] text-white/70 px-3 py-1 rounded-full text-xs font-normal border border-white/[0.08]">
                  Payment Guide
                </span>
                <span className="text-white/40 text-xs">
                  Updated November 2026
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                How to Finance Airline Tickets Without Credit Cards
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Travelers increasingly want ways to book flights in installments without relying on traditional credit cards. The good news: you can pay for airline tickets in installments using a BNPL travel app, PayPal's installment options, deposit-based layaway, airline gift cards, debit, or even cash at ticket counters. Below, we outline practical paths to flexible flight financing, how they work, and when each option fits best.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Key Points Summary */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1d1d1f] border border-white/[0.08] rounded-[1.25rem] p-6 md:p-8"
            >
              <p className="text-white/80 leading-relaxed">
                We also highlight <strong className="text-white">Defera's transparent, customer-first approach—so you can book confidently, avoid hidden fees, and keep your budget on track as you plan your trip</strong>.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Defera Section */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-white/70" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Use Defera to Pay for Flights Over Time
                </h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                Defera lets you book flights now and pay over time in automatic weekly or bi-weekly payments, with no credit check and no interest. Defera isn’t a lender: it’s a layaway-style payment plan. You pay a deposit when you book (as low as 5% on trips 90+ days out), and the trip total shown at checkout already includes every fee, so nothing is added later. Once your booking is approved, your agreed-upon trip price is secured, and your airline confirmation number arrives once the plan is paid in full. Trips must depart at least 22 days out, so it suits planned trips, like a student’s first international trip, rather than last-minute airfare.
              </p>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80"><strong className="text-white">Book your flight now</strong> and spread payments over time to smooth cash flow.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80"><strong className="text-white">Choose weekly or bi-weekly payments</strong> to match your paycheck cycle.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80"><strong className="text-white">Make informed decisions</strong> with transparent terms before you commit.</span>
                </li>
              </ul>

              <p className="text-white/80 leading-relaxed mb-6">
                Get started with Defera and pay for airline tickets in installments with control and clarity at every step.
              </p>

              <Button
                onClick={() => navigate('/search')}
                className="bg-white hover:bg-white/90 text-black px-6 py-3 rounded-full font-semibold text-sm"
              >
                Try Defera Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Debit Cards Section */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Wallet className="w-7 h-7 text-white/55" />
                Book Flights with Debit Cards
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Paying with a debit card pulls funds directly from your bank account—no interest, no revolving debt, and no credit check. Many airlines accept debit cards, and some also accept their own gift cards. Cash is now rare: JetBlue, for example, says most of its airport lobbies don’t accept cash, per its <a href="https://www.jetblue.com/help/payment-options" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-300 underline">payment options page</a>. This path is best for budget-conscious travelers who want to avoid financing altogether or for those with limited credit history.
              </p>

              <h3 className="text-lg font-semibold text-white mb-4">Tips:</h3>
              <ul className="space-y-3 text-white/80">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span>Confirm your daily debit limit before purchase.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span>Enable travel notifications to avoid declines.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span>Keep a small buffer for baggage or seat fees.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* PayPal Section */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <CreditCard className="w-7 h-7 text-white/70" />
                Pay for Airline Tickets Using PayPal Installments
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                PayPal offers two flexible options that help you book flights without a credit card and split the cost over time. As summarized in <a href="https://www.skyscanner.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-300 underline">Skyscanner's BNPL guide</a>, <strong className="text-white">Pay in 4</strong> divides your purchase into four interest-free biweekly payments, while <strong className="text-white">Pay Monthly</strong> offers a longer term with a fixed interest rate and $0 down for eligible purchases. As of November 2026, PayPal lists Pay in 4 for purchases of $10 to $2,000 and Pay Monthly for $49 to $10,000 over 3 to 24 months. Many airlines and travel sites support PayPal at checkout, giving you a broad reach for domestic and international routes.
              </p>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">PayPal for flights: quick comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left py-3 px-4 text-white font-semibold">Option</th>
                        <th className="text-left py-3 px-4 text-white font-semibold">Schedule</th>
                        <th className="text-left py-3 px-4 text-white font-semibold">Interest</th>
                        <th className="text-left py-3 px-4 text-white font-semibold">Upfront Payment</th>
                        <th className="text-left py-3 px-4 text-white font-semibold">Typical Eligibility</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paypalComparison.map((row, index) => (
                        <tr key={index} className="border-b border-white/10">
                          <td className="py-3 px-4 text-white/90 font-medium">{row.option}</td>
                          <td className="py-3 px-4 text-white/80">{row.schedule}</td>
                          <td className="py-3 px-4 text-white/80">{row.interest}</td>
                          <td className="py-3 px-4 text-white/80">{row.upfront}</td>
                          <td className="py-3 px-4 text-white/80">{row.eligibility}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-4 mt-4">
                  <p className="text-white/60 text-xs">
                    <strong className="text-white">Note:</strong> Approval and terms vary by location and basket size.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Gift Cards Section */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Gift className="w-7 h-7 text-brand-300" />
                Buy Airline Gift Cards to Manage Travel Expenses
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Gift cards can serve as a budgeting tool and an alternative payment method when you don't want to use or don't have a credit card. Some airline gift cards are sold in retail stores as well as online (Delta’s, for example), and as of November 2026 Delta and Southwest gift cards don’t expire. They can be an easy way to cap spending for students, families, or as a travel gift. Check each airline’s rules first: <a href="https://www.delta.com/us/en/gift-cards/overview" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-300 underline">Delta’s gift cards</a>, for example, can’t pay for bag fees or upgrades.
              </p>

              <h3 className="text-lg font-semibold text-white mb-4">Why this works:</h3>
              <ul className="space-y-3 text-white/80">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-300 flex-shrink-0 mt-0.5" />
                  <span>Set a firm airfare budget before you shop.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-300 flex-shrink-0 mt-0.5" />
                  <span>Reduce the risk of overspending on extras.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-300 flex-shrink-0 mt-0.5" />
                  <span>Great for funding trips over time with small contributions.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Cash Payments Section */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <DollarSign className="w-7 h-7 text-green-400" />
                Use Cash Payments Where Available
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Cash is increasingly rare at airline counters, so confirm with the airline before you go. JetBlue, for example, says most of its airport lobbies don’t accept cash; kiosks there can turn cash into a prepaid card for a fee (per its <a href="https://www.jetblue.com/help/payment-options" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-300 underline">payment options page</a>, as of November 2026). Buying at the airport typically requires in-person payment and valid ID, and some airlines add a service charge for tickets bought at the counter or by phone. Where it’s still available, this option can help if you’re unbanked or prefer to avoid cards.
              </p>

              <h3 className="text-lg font-semibold text-white mb-4">What to expect:</h3>
              <ul className="space-y-3 text-white/80">
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Bring government-issued ID.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Expect real-time fare availability and potential service fees.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Consider arriving early to finalize payment before fare changes.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* BNPL Section */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Zap className="w-7 h-7 text-brand-400" />
                Explore Buy Now, Pay Later Travel Apps and Platforms
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                BNPL lets you book flights now and pay over time through third-party apps, travel agencies, or embedded checkout options on airline and OTA sites. Common features include instant or near-instant approvals, interest-free plans (for short-term options), automated payments, and flexible timelines. These tools can be valuable if you want to lock in a fare today but prefer to spread the cost.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    Pros:
                  </h4>
                  <ul className="space-y-2 text-white/80 text-sm">
                    <li>• Predictable payments and quick decisions.</li>
                    <li>• Access to promo rates and occasional interest-free plans.</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    Cons:
                  </h4>
                  <ul className="space-y-2 text-white/80 text-sm">
                    <li>• Missed payments can trigger late fees.</li>
                    <li>• Longer-term plans may charge interest; always compare total costs.</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* How BNPL Works Section */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                How BNPL Works for Airline Tickets
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                BNPL splits your airfare into scheduled repayments—often biweekly or monthly—so you can secure tickets upfront and pay gradually.
              </p>

              <h3 className="text-lg font-semibold text-white mb-4">Typical steps:</h3>
              <ol className="space-y-3 text-white/70 mb-6 list-decimal list-inside">
                <li>Apply at checkout or in-app and get a quick eligibility decision (usually 18+, valid ID/residency; some providers run a soft credit check).</li>
                <li>Choose a plan (e.g., biweekly Pay in 4 or monthly financing).</li>
                <li>Complete booking. With most BNPL, the lender pays the merchant and your booking is confirmed right away; with Defera, your trip price is secured once your booking is approved and your airline confirmation number arrives once the plan is paid in full.</li>
                <li>Repay automatically through linked bank, card, or wallet.</li>
              </ol>

              <p className="text-white/80 leading-relaxed">
                Timelines vary by provider: Affirm offers 3–60 month plans, while Klarna extends up to 24 months, according to <a href="https://www.travelandleisure.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-300 underline">Travel + Leisure</a>. Short-term pay-in-4 options are often interest-free; longer terms may include a fixed APR.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Popular BNPL Providers Section */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Popular BNPL Travel Apps for Flight Installments
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Below is a quick look at leading options, their term structures, and standout features for booking flights on installments.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-semibold">Provider/Platform</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Installment Structure</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Interest/Fees Model</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Where You Can Book</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Notable Features</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bnplProviders.map((row, index) => (
                      <tr 
                        key={index} 
                        className={`border-b border-white/10 ${row.provider === 'Defera' ? 'bg-white/[0.04]' : ''}`}
                      >
                        <td className={`py-3 px-4 ${row.provider === 'Defera' ? 'text-white/70 font-semibold' : 'text-white/90'}`}>
                          {row.provider}
                        </td>
                        <td className="py-3 px-4 text-white/80">{row.structure}</td>
                        <td className="py-3 px-4 text-white/80">{row.interest}</td>
                        <td className="py-3 px-4 text-white/80">{row.where}</td>
                        <td className="py-3 px-4 text-white/80">{row.features}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-4">
                <p className="text-white/60 text-xs">
                  <strong className="text-white">Note:</strong> Availability, terms, and eligibility vary by market and basket size.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Benefits of BNPL Options for Budget-Conscious Travelers
              </h2>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Spread large or urgent airfare costs into manageable payments.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Access interest-free options (e.g., pay-in-4) and clear schedules for better budgeting.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Compare transparent terms and avoid revolving-credit surprises.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Book earlier to secure better fares and seat choices.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">For layaway-style services, some providers, like Paylater Travel in Australia and Defera in the U.S., advertise no credit checks and no interest, and you finish paying before you fly, which can provide extra peace of mind.</span>
                </li>
              </ul>

              {/* Consumer Caution Box */}
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-white/90" />
                  Consumer caution
                </h4>
                <p className="text-white/80 text-sm">
                  BNPL can still carry risks such as late fees and overextension, and dispute protections may be weaker than with credit cards: the <a href="https://www.consumerfinance.gov/about-us/newsroom/cfpb-study-details-the-rapid-growth-of-buy-now-pay-later-lending/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-300 underline">CFPB’s 2022 study</a> found BNPL loans offer minimal dispute resolution rights. Always review total costs and policies before you commit.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Deposit and Payment Plans Section */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Lock in Flight Prices with Deposit and Payment Plans
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Deposit-based layaway locks your fare with a small upfront payment, completing the balance in installments before travel. It's useful when you've found a good deal but need time to finish payments. Providers such as Paylater Travel in Australia focus on flexible schedules with no interest and confirm your flights after the final payment; with Defera, your trip price is secured once your booking is approved and your airline confirmation number arrives once the plan is paid in full. For how deposits, fare holds and price freezes compare, see our guide to <Link to="/guides/lock-flight-price-with-deposit" className="text-white/80 hover:text-white underline">locking in a flight price with a deposit</Link>.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    Pros:
                  </h4>
                  <ul className="space-y-2 text-white/80 text-sm">
                    <li>• Price protection during your payment window.</li>
                    <li>• No interest in many layaway models.</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    Cons:
                  </h4>
                  <ul className="space-y-2 text-white/80 text-sm">
                    <li>• Booking confirmation usually comes only after the final payment.</li>
                    <li>• Itineraries may be less flexible until fully paid.</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Loyalty Programs Section */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Award className="w-7 h-7 text-brand-400" />
                Utilize Airline and Loyalty Program Options Without Credit Cards
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Frequent flyer programs let you use miles or points to reduce or fully cover airfare without a credit card. Some airlines and agencies also offer "hold my fare" services for a limited period, which can help you finalize logistics before paying in full; see our guide to <Link to="/guides/airline-fare-hold-policies" className="text-white/80 hover:text-white underline">airline fare hold policies</Link> for how holds work. These options work best for advance planners and travelers coordinating visas, events, or group trips.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Tips to Choose Section */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Tips to Choose the Best Financing Method for Your Flight
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Match your situation to the right payment path by weighing total costs, fees, eligibility, and timing.
              </p>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Quick chooser</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left py-3 px-4 text-white font-semibold">Your situation</th>
                        <th className="text-left py-3 px-4 text-white font-semibold">Best-fit options</th>
                        <th className="text-left py-3 px-4 text-white font-semibold">Why it helps</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quickChooser.map((row, index) => (
                        <tr key={index} className="border-b border-white/10">
                          <td className="py-3 px-4 text-white/90 font-medium">{row.situation}</td>
                          <td className="py-3 px-4 text-white/80">{row.options}</td>
                          <td className="py-3 px-4 text-white/80">{row.why}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-white/80 leading-relaxed">
                Focus on providers with transparent pricing, clear schedules, and responsive support. If interest applies, compare the total cost versus paying with savings or a debit card.
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/20 transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to book without a credit card?
              </h2>
              <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl mx-auto">
                Pay for flights with Defera in weekly or bi-weekly payments, with no credit check and no interest.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={() => navigate('/search')}
                  className="bg-white hover:bg-white/90 text-black px-8 py-6 rounded-full font-semibold text-base w-full sm:w-auto"
                >
                  Search Flights with Defera
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="text-white/60 hover:text-white text-sm font-medium transition-colors"
                >
                  Learn how it works →
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-white/40 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-white/55" />
                  <span>No credit card needed</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-white/55" />
                  <span>No credit check</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-white/55" />
                  <span>No interest</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/flight-layaway-vs-bnpl" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight layaway vs BNPL: what’s the difference?
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/lock-flight-price-with-deposit" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  How to lock in a flight price with a deposit
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-payment-plans-no-credit-check" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight payment plans with no credit check
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/pay-for-flights-with-debit-card" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  How to pay for flights with a debit card
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
}

